/**
 * Typed wrappers for the v4 admin Callables (zero client writes - every
 * mutation goes through one of these). Names and shapes mirror the
 * blume-tools functions backend (`v4_*`, region us-east4 - the region pin
 * lives in `core/firebase.ts`); the backend's input validation rejects any
 * field outside the sets below, so these parameter types ARE the contract.
 *
 * Every wrapper throws `CallableError`, which carries the raw Firebase code
 * plus a ready `userMessage` from the central mapper - screens catch and
 * toast `error.userMessage` (or call `toUserMessage` on anything unknown).
 */
import { FirebaseError } from 'firebase/app';
import { httpsCallable, type HttpsCallableOptions } from 'firebase/functions';

import { functions } from '../core/firebase';
import type {
  DomainImportMode,
  DomainImportRow,
  ImportPartnerDomainsResult,
} from './domain-import';
import type {
  BlumeTeam,
  SupportRequestMessage,
  SupportRequestNote,
  SupportRequestPriority,
  SupportRequestStatus,
  SupportRequestType,
  LicenseModule,
  PartnerType,
  PermissionRoleId,
} from './schema';
import type { ReportGoal, ReportObservation } from './student-report/student-progress';

// ------------------------------------------------------- params + results

export interface AdminCreatePartnerParams {
  name: string;
  type: PartnerType;
  state?: string;
  phone?: string;
  address?: string;
  studentPopulation?: number;
  /** Whole-array seed at creation - the add-partner wizard's basic-info step. */
  schools?: string[];
  /**
   * Licences created WITH the partner, in the same document write.
   *
   * Licences are a FIELD on the partner doc, so sending them here makes the
   * whole creation atomic by construction - the partner and its licences land
   * together or neither does. The wizard used to create the partner and then
   * call adminAddPartnerLicense once per licence, which is N+1 writes to the
   * same document and leaves a window where a partner exists holding only some
   * of what was asked for.
   */
  licenses?: {
    module: LicenseModule;
    /** Start of coverage - ms since epoch (UTC midnight of the chosen date). */
    start: number;
    /** End of coverage - ms since epoch; must be after `start`. */
    end: number;
    /** Annual fee in dollars. */
    annualFee: number;
    trial: boolean;
    notes?: string;
  }[];
}
export interface AdminCreatePartnerResult {
  partnerId: string;
  /** Ids of the licences written alongside the partner, in order. */
  licenseIds: string[];
}

/** Identity fields plus the whole-array extras - licenses and contacts have their own doors. */
export interface AdminUpdatePartnerParams {
  partnerId: string;
  name?: string;
  type?: PartnerType;
  state?: string;
  phone?: string;
  address?: string;
  studentPopulation?: number;
  /** Staff estimate of students on caseloads; null clears it. */
  estimatedCaseload?: number | null;
  /** Whole-array replace, matching how the app edits the list. */
  schools?: string[];
  allowStudentLookup?: boolean;
  /** Domain-based invitations on or off; null returns it to unset (on). */
  domainAuthEnabled?: boolean | null;
  /** Whole-array replace; the server cleans and checks every domain. */
  domains?: string[];
}
export interface AdminUpdatePartnerResult {
  ok: true;
}

export interface AdminAddPartnerLicenseParams {
  partnerId: string;
  module: LicenseModule;
  /** Start of coverage - ms since epoch (UTC midnight of the chosen date). */
  start: number;
  /** End of coverage - ms since epoch; must be after `start`. */
  end: number;
  /** Annual fee in dollars. */
  annualFee: number;
  trial: boolean;
  notes?: string;
}
export interface AdminAddPartnerLicenseResult {
  licenseId: string;
}

/** The module is the license's identity - it can't change (delete and re-add). */
export interface AdminUpdatePartnerLicenseParams {
  partnerId: string;
  licenseId: string;
  start?: number;
  end?: number;
  annualFee?: number;
  trial?: boolean;
  /** Empty string clears the notes. */
  notes?: string;
}
export interface AdminUpdatePartnerLicenseResult {
  ok: true;
}

export interface AdminDeletePartnerLicenseParams {
  partnerId: string;
  licenseId: string;
}
export interface AdminDeletePartnerLicenseResult {
  ok: true;
}

export interface AdminAddPartnerContactParams {
  partnerId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  jobTitle?: string;
  /** True demotes any other primary contact - one primary per partner. */
  isPrimary?: boolean;
  notes?: string;
}
export interface AdminAddPartnerContactResult {
  contactId: string;
}

export interface AdminUpdatePartnerContactParams {
  partnerId: string;
  contactId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  /** Empty string clears the field. */
  phone?: string;
  /** Empty string clears the field. */
  jobTitle?: string;
  /** True demotes any other primary contact - one primary per partner. */
  isPrimary?: boolean;
  /** Empty string clears the field. */
  notes?: string;
}
export interface AdminUpdatePartnerContactResult {
  ok: true;
}

export interface AdminDeletePartnerContactParams {
  partnerId: string;
  contactId: string;
}
export interface AdminDeletePartnerContactResult {
  ok: true;
}

export interface AdminDeletePartnerParams {
  partnerId: string;
}
export interface AdminDeletePartnerResult {
  ok: true;
}

/** Mints the Auth user + the `users/{emailLowercase}` doc together. */
export interface AdminCreateAccountParams {
  email: string;
  firstName: string;
  lastName: string;
}
export interface AdminCreateAccountResult {
  uid: string;
}

/** The email is the key (`users/{emailLowercase}`) - it identifies, never changes. */
export interface AdminUpdateAccountParams {
  email: string;
  firstName?: string;
  lastName?: string;
}
export interface AdminUpdateAccountResult {
  ok: true;
}

export interface AdminDeleteAccountParams {
  email: string;
}
export interface AdminDeleteAccountResult {
  ok: true;
}

/** Invite an email to ONE partner - the only door for new memberships. */
export interface AdminInviteUserParams {
  email: string;
  partnerId: string;
  role: PermissionRoleId;
  jobTitle?: string;
}
export interface AdminInviteUserResult {
  inviteId: string;
  /** False = the invite exists but the email could not be sent (resend later). */
  emailSent: boolean;
}

/** Both invite refs are (email, partnerId) - one pending invite per pair. */
export interface AdminInviteRefParams {
  email: string;
  partnerId: string;
}
export interface AdminInviteRefResult {
  ok: true;
}

/** Account-level sign-in block (Firebase Auth) - not the per-partner flag. */
export interface AdminSetAccountDisabledParams {
  email: string;
  disabled: boolean;
}
export interface AdminSetAccountDisabledResult {
  ok: true;
}

export interface AdminRemoveUserFromPartnerParams {
  email: string;
  partnerId: string;
}
export interface AdminRemoveUserFromPartnerResult {
  ok: true;
  deletedProfile: boolean;
  updatedAccount: boolean;
  claimSynced: boolean;
}

/** The per-partner soft flag (the app's semantics) - membership survives. */
export interface AdminSetPartnerUserDeactivatedParams {
  email: string;
  partnerId: string;
  deactivated: boolean;
}
export interface AdminSetPartnerUserDeactivatedResult {
  ok: true;
}

export interface AdminVerifyAccountParams {
  email: string;
}
export interface AdminVerifyAccountResult {
  ok: true;
  alreadyVerified?: boolean;
}

// -------------------------------------------------- error -> user message

/**
 * Firebase error codes -> what the screen should tell the admin.
 *
 * Two families, because two transports: Callables raise `functions/*`, while
 * the partner writes that now go straight to Firestore (see partner-writes.ts)
 * raise the BARE gRPC codes. Without the bare entries a rules rejection would
 * fall through to the generic fallback and say nothing useful.
 */
const USER_MESSAGES: Readonly<Record<string, string>> = {
  // Firestore SDK (direct writes)
  'permission-denied':
    'You do not have permission to do that, or the change was rejected as invalid.',
  unauthenticated: 'Your session has expired - sign in again.',
  'not-found': 'That record no longer exists.',
  'already-exists': 'That already exists.',
  'failed-precondition': 'That action is not allowed in the current state.',
  aborted: 'Someone else changed that at the same time - try again.',
  unavailable: 'Blume could not be reached - check your connection and try again.',
  'deadline-exceeded': 'The request timed out - try again.',
  internal: 'Something went wrong on our side - try again in a moment.',
  // Callables
  'functions/permission-denied': 'You do not have permission to do that.',
  'functions/unauthenticated': 'Your session has expired - sign in again.',
  'functions/invalid-argument':
    'Something in that request is not valid - check the fields and try again.',
  'functions/not-found': 'That record no longer exists.',
  'functions/already-exists': 'That already exists.',
  'functions/failed-precondition': 'That action is not allowed in the current state.',
  'functions/unavailable': 'Blume could not be reached - check your connection and try again.',
  'functions/deadline-exceeded': 'The request timed out - try again.',
  'functions/internal': 'Something went wrong on our side - try again in a moment.',
};

/**
 * The two STATE-CONFLICT codes carry a user-grade sentence from the backend
 * ("An account already exists for x@y.", "x@y's invite is still pending -
 * revoke the invite instead."). That sentence names the actual conflict, so
 * it beats any one-size generic; the map entries above stay as the fallback
 * for infra-generated errors that carry no usable message.
 */
const PREFER_SERVER_MESSAGE = new Set([
  'functions/already-exists',
  'functions/failed-precondition',
]);

const FALLBACK_MESSAGE = 'Something went wrong - try again.';

/** The one place Callable failures become copy the UI can show. */
export function toUserMessage(error: unknown): string {
  if (error instanceof CallableError) return error.userMessage;
  if (error instanceof FirebaseError) {
    if (PREFER_SERVER_MESSAGE.has(error.code) && looksLikeSentence(error.message)) {
      return error.message;
    }
    return USER_MESSAGES[error.code] ?? FALLBACK_MESSAGE;
  }
  return FALLBACK_MESSAGE;
}

/** A server message is usable when it reads like copy, not a bare code. */
function looksLikeSentence(message: string | undefined): boolean {
  return typeof message === 'string' && message.trim().includes(' ');
}

/** What every wrapper throws: the raw code plus ready-to-toast copy. */
export class CallableError extends Error {
  constructor(
    /** e.g. 'functions/permission-denied', or 'unknown'. */
    readonly code: string,
    readonly userMessage: string,
    options?: ErrorOptions,
  ) {
    super(userMessage, options);
    this.name = 'CallableError';
  }
}

function toCallableError(error: unknown): CallableError {
  const code = error instanceof FirebaseError ? error.code : 'unknown';
  return new CallableError(code, toUserMessage(error), { cause: error });
}

// ------------------------------------------------------------- wrappers

function adminCallable<TParams, TResult>(
  name: string,
  options?: HttpsCallableOptions,
): (params: TParams) => Promise<TResult> {
  return async (params: TParams): Promise<TResult> => {
    try {
      const result = await httpsCallable<TParams, TResult>(functions(), name, options)(params);
      return result.data;
    } catch (error) {
      throw toCallableError(error);
    }
  };
}

// ------------------------------------------------------- staff registry

/** One row of the Admins table. Mirrors StaffRow in ./staff-roles. */
export interface AdminListStaffResult {
  admins: {
    uid: string;
    email: string;
    authEmail?: string;
    roles: string[];
    active: boolean;
    grantedAt?: number;
    grantedBy?: string;
    revokedAt?: number;
    revokedBy?: string;
    claimInSync: boolean;
    disabled: boolean;
    /** Partners their Auth account belongs to. Deleting an admin deletes that
     *  account, so a non-zero count means the delete reaches beyond the
     *  console; the confirmation says so. */
    partnerCount?: number;
  }[];
}

export interface AdminSetStaffRolesParams {
  /** Must end @blumemethod.com - re-checked server-side against the resolved
   *  Auth record, not just this payload. */
  email: string;
  roles: string[];
}

export interface AdminSetStaffRolesResult {
  ok: true;
  uid: string;
  /** False when the roles already matched - nothing was written. */
  changed: boolean;
  /** An Auth user was created for this address; they have no password yet. */
  created: boolean;
}

/** Keyed by uid, not email: the registry doc IS keyed by uid, and an email
 *  lookup adds a "their address changed, you revoked the wrong person" case. */
export interface AdminRevokeStaffParams {
  uid: string;
}

export interface AdminRevokeStaffResult {
  ok: true;
  changed: boolean;
}

/** Keyed by uid for the same reason revoke is - and it matters more here,
 *  because deleting the wrong person cannot be undone. */
export interface AdminDeleteStaffParams {
  uid: string;
}

/** No input: the dataset is generated and lives beside the Callable, so there
 *  is nothing for a client to supply - and a bulk writer that took paths from
 *  one would be a write-anything primitive with a friendly name. */
export type AdminPopulateLaunchParams = Record<string, never>;

export interface AdminPopulateLaunchResult {
  ok: true;
  written: number;
  /** One line per document, for the page's creation log. */
  log: string[];
}

export interface AdminDeleteStaffResult {
  ok: true;
  deletedDoc: boolean;
  deletedAuth: boolean;
}

/**
 * Partner identity and contacts, back through Callables.
 *
 * They moved to direct SDK writes when `isSuper()` in the rules was the same
 * check the Callable made. Staff roles ended that: rules can only test a CLAIM,
 * and a claim survives on a cached ID token for up to an hour after a role is
 * taken away. Tolerable for super_admin, which is granted rarely; not for
 * `developer`, which people gain and lose. These re-read the live registry
 * doc per call, so revocation lands at once.
 */
export const adminCreatePartner = adminCallable<AdminCreatePartnerParams, AdminCreatePartnerResult>(
  'blumeAdmin_CreatePartner',
);

export const adminUpdatePartner = adminCallable<AdminUpdatePartnerParams, AdminUpdatePartnerResult>(
  'blumeAdmin_UpdatePartner',
);

/**
 * Import domains page: `dryRun: true` previews, `false` applies. `mode` is
 * `add` (never removes) or `replace` (each partner the file names matches it).
 * Needs partners.write.
 */
export interface AdminImportPartnerDomainsParams {
  rows: DomainImportRow[];
  dryRun: boolean;
  mode: DomainImportMode;
}

export const adminImportPartnerDomains = adminCallable<
  AdminImportPartnerDomainsParams,
  ImportPartnerDomainsResult
>('blumeAdmin_ImportPartnerDomains');

export const adminAddPartnerContact = adminCallable<
  AdminAddPartnerContactParams,
  AdminAddPartnerContactResult
>('blumeAdmin_AddPartnerContact');

export const adminUpdatePartnerContact = adminCallable<
  AdminUpdatePartnerContactParams,
  AdminUpdatePartnerContactResult
>('blumeAdmin_UpdatePartnerContact');

export const adminDeletePartnerContact = adminCallable<
  AdminDeletePartnerContactParams,
  AdminDeletePartnerContactResult
>('blumeAdmin_DeletePartnerContact');

/** Delete a partner - the backend owns the cascade (subcollections, links). */
export const adminDeletePartner = adminCallable<AdminDeletePartnerParams, AdminDeletePartnerResult>(
  'blumeAdmin_DeletePartner',
);

/**
 * Licences, back through Callables.
 *
 * These three briefly moved to direct SDK writes with the rest of the partner
 * writes, then came back when staff roles landed. Revenue admins need to write
 * licences, and firestore.rules can only check a CLAIM - which survives on a
 * cached ID token for up to an hour after the role is removed. The Callable
 * re-reads the live `blumeStaff/{uid}` doc on every call, so losing Revenue
 * stops licence writes immediately.
 *
 * Partner identity and contacts stay in partner-writes.ts: still super-only,
 * still client-side.
 */
export const adminAddPartnerLicense = adminCallable<
  AdminAddPartnerLicenseParams,
  AdminAddPartnerLicenseResult
>('blumeAdmin_AddPartnerLicense');

export const adminUpdatePartnerLicense = adminCallable<
  AdminUpdatePartnerLicenseParams,
  AdminUpdatePartnerLicenseResult
>('blumeAdmin_UpdatePartnerLicense');

export const adminDeletePartnerLicense = adminCallable<
  AdminDeletePartnerLicenseParams,
  AdminDeletePartnerLicenseResult
>('blumeAdmin_DeletePartnerLicense');

/**
 * The staff registry - the ONLY way the console can see or change who has
 * access.
 *
 * `blumeStaff` is `allow read, write: if false` in firestore.rules, for
 * everyone including supers: a client that could write it could mint staff.
 * So even LISTING admins is a Callable, and all three are super-only.
 */
export const adminListStaff = adminCallable<Record<string, never>, AdminListStaffResult>(
  'blumeAdmin_ListStaff',
);

/** Add an admin or change their roles - one door, keyed by address. */
export const adminSetStaffRoles = adminCallable<AdminSetStaffRolesParams, AdminSetStaffRolesResult>(
  'blumeAdmin_SetStaffRoles',
);

/** Revoke all access. The doc is kept and flipped inactive, never deleted. */
export const adminRevokeStaff = adminCallable<AdminRevokeStaffParams, AdminRevokeStaffResult>(
  'blumeAdmin_RevokeStaff',
);

// ------------------------------------------------- lead administrator

/**
 * Designate (or clear, with `email: null`) a partner's Lead Administrator -
 * the one member who may accept or decline V3 for them.
 *
 * Firestore keeps `partners/{id}/governance/**` at `allow write: if false`
 * for every client, super included, so this is the only way to set it. The
 * Callable writes the designation to the PARTNER doc (which gates nothing,
 * so a lead can be named long before the partner is asked) and syncs the
 * governance record only where one already exists - creating one would drop
 * the partner into V2 Compatibility Mode.
 */
export const adminSetLeadAdministrator = adminCallable<
  { partnerId: string; email: string | null },
  { ok: true }
>('blumeAdmin_SetLeadAdministrator');

/** One partner's V3 governance state, flattened by the Callable. */
export interface PartnerGovernanceView {
  /** False when the partner has no `governance/v3Decision` document at all. */
  inProgram: boolean;
  /** Raw stored status; '' when there is no record. Bucket it, never trust it. */
  status: string;
  decisionVersion: number | null;
  /** Epoch ms of the decision; null while pending or with no record. */
  decidedAt: number | null;
  /** Who the record says decided; null when nobody has. */
  decidedByEmail: string | null;
  /** The designation of record, from the PARTNER document. */
  leadAdministratorEmail: string | null;
  /** The governance record's own copy; null when absent or no record. */
  recordLeadAdministratorEmail: string | null;
  /** How the current lead was designated, e.g. MIGRATION_BACKFILL. */
  leadAdministratorBasis: string | null;
}

/**
 * One partner's governance record, read live.
 *
 * A Callable for the same reason as the partner feed: firestore.rules gates
 * `partners/{id}/governance/**` on `canAccessPartner`, which does not admit
 * Blume staff. Reads the record fresh rather than through the V3 mirror, so
 * the Settings section shows the true state.
 */
export const adminGetPartnerGovernance = adminCallable<
  { partnerId: string },
  PartnerGovernanceView
>('blumeAdmin_GetPartnerGovernance');

/**
 * Mark a partner's V3 terms accepted on behalf of their Lead Administrator,
 * for a partner who agreed out of band.
 *
 * Recorded as the Lead Administrator's own decision, so every downstream
 * reader sees one consistent kind of acceptance; the console operator is
 * captured in the governance event and the staff activity log. Requires an
 * existing governance record and a designated lead, and never creates a
 * record (creating one is what starts Compatibility Mode).
 */
export const adminRecordV3Acceptance = adminCallable<
  { partnerId: string },
  { status: 'ACCEPTED'; applied: boolean }
>('blumeAdmin_RecordV3Acceptance');

// ------------------------------------------------------ support requests

/** The triage dialog's one Save: status + assignment together. */
export interface TriageSupportRequestParams {
  id: string;
  status: SupportRequestStatus;
  /** Re-categorise a mis-filed request. */
  type: SupportRequestType;
  /** The Help Center's own ladder, or null for no priority. */
  priority: SupportRequestPriority | null;
  /** Owning Blume team, or null to clear. */
  assignedTeam: BlumeTeam | null;
  /** Assignee's email (must be active staff - the server checks), or null. */
  assigneeEmail: string | null;
}

/**
 * The support triage writes. firestore.rules keeps `support_requests` update
 * super-only (the legacy portal), so for the console these Callables are the
 * ONLY way a ticket moves - and they re-read the staff registry per call, so
 * a revoked role stops triaging immediately.
 */
export const adminTriageSupportRequest = adminCallable<TriageSupportRequestParams, { ok: true }>(
  'blumeAdmin_TriageSupportRequest',
);

/** Append a note. Authorship is stamped server-side from the caller. */
export const adminAddSupportNote = adminCallable<
  { id: string; message: string },
  { note: SupportRequestNote }
>('blumeAdmin_AddSupportNote');

/** Remove a note, matched by value - pass back the stored object. */
export const adminRemoveSupportNote = adminCallable<
  { id: string; note: SupportRequestNote },
  { ok: true }
>('blumeAdmin_RemoveSupportNote');

/** The console's reply to a submitter: one Blume Support Team message. */
export interface RespondSupportRequestParams {
  id: string;
  /** The reply body - required (a message is what earns the allowResponse gate). */
  message: string;
  /** Open the ticket for ONE user reply. Defaults true in the compose UI. */
  allowResponse: boolean;
}

/**
 * Post a Blume Support Team message on a ticket and set the one-reply gate.
 * Appending the message is what triggers the user's update email (server-side).
 * Returns the stored message + resulting gate so the confirmation modal can
 * show the updated ticket without a re-read.
 */
export const adminRespondSupportRequest = adminCallable<
  RespondSupportRequestParams,
  { message: SupportRequestMessage; allowResponse: boolean }
>('blumeAdmin_RespondToSupportRequest');

/**
 * Stamp the calling admin's read time on a ticket (`lastReadByAdmin[email]`),
 * clearing its unread flag for that admin until the next user message. Any
 * active staff may call it — it records that THEY looked, not a mutation.
 */
export const adminMarkSupportTicketRead = adminCallable<
  { id: string },
  { ok: true; readAt: number }
>('blumeAdmin_MarkSupportTicketRead');

/** One assignable person; `name` is '' when Auth has no display name. */
export interface SupportAssignee {
  email: string;
  name: string;
}

/** The assignee picker's roster: active staff as email + name. A narrow
 *  projection so the full registry (staff.read) can stay super-only. */
export const adminListSupportAssignees = adminCallable<
  Record<string, never>,
  { assignees: SupportAssignee[] }
>('blumeAdmin_ListSupportAssignees');

// ------------------------------------------------- V3 governance decisions

/** One partner's V3 governance record, flattened by the callable. */
export interface V3DecisionRow {
  partnerId: string;
  /** Raw stored status - bucket it via v3-conversion.ts, never trust it. */
  status: string;
  /** Epoch ms of the decision; null while PENDING (or legacy records). */
  decidedAt: number | null;
  /** The designated Lead Administrator - the person to chase while PENDING. */
  leadAdministratorEmail: string | null;
}

/**
 * Every partner's `governance/v3Decision`, one row per EXISTING record.
 *
 * Partners with no record are deliberately absent - the client derives that
 * bucket by diffing against the partner list it already holds. Same
 * cannot-be-a-direct-read reason as the partner feed: `governance` is gated
 * on `canAccessPartner`, which does not admit staff.
 */
export const adminListV3Decisions = adminCallable<
  Record<string, never>,
  { rows: V3DecisionRow[]; generatedAt: number }
>('blumeAdmin_ListV3Decisions');

// --------------------------------------------------------- district counts

/** One district's head-counts, as the analytics dashboards need them. */
export interface CoverageCountsResultRow {
  partnerId: string;
  /** Students in the district. */
  students: number;
  /** Distinct students with at least one active goal. */
  studentsWithActiveGoal: number;
}

/**
 * Student coverage counts per district for the analytics dashboards.
 *
 * A Callable for one reason: student and goal records are readable only by a
 * district's own members and Blume supers, and opening them to every staff
 * role just to count them would expose student records the console never
 * shows. The server returns numbers only. Member and educator counts are NOT
 * here - staff can read member documents, so the console counts those itself
 * with aggregation queries (analytics-store.ts).
 */
export const adminGetCoverageCounts = adminCallable<
  Record<string, never>,
  { rows: CoverageCountsResultRow[]; computedAt: number }
>('blumeAdmin_GetCoverageCounts');

// ------------------------------------------------------- student report

/** Which student's records to fetch for the student report. */
export interface AdminGetStudentReportDataParams {
  partnerId: string;
  studentId: string;
}

/** One student's records, only the fields the student report draws. */
export interface AdminGetStudentReportDataResult {
  student: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    grade: string | null;
    school: string | null;
  };
  goals: ReportGoal[];
  observations: ReportObservation[];
}

/**
 * One student's goals and observations for the student report. Super admins
 * only; the function records the download in the staff activity log.
 * @security FERPA - returns one student's records
 */
export const adminGetStudentReportData = adminCallable<
  AdminGetStudentReportDataParams,
  AdminGetStudentReportDataResult
>('blumeAdmin_GetStudentReportData');

/** One row of the Students tab. */
export interface AdminListStudentsRow {
  id: string;
  firstName: string | null;
  lastName: string | null;
  grade: string | null;
  school: string | null;
}

/**
 * A district's current (not archived) students: id, name, grade and school
 * only. `students.read` (super only); the server reads with a field mask so
 * nothing else leaves Firestore.
 * @security FERPA - returns student names, grades and schools
 */
export const adminListStudents = adminCallable<
  { partnerId: string },
  { students: AdminListStudentsRow[] }
>('blumeAdmin_ListStudents');

// ---------------------------------------------------------- own onboarding

/** The caller's own welcome-tour stamp. `null` means they have not finished it. */
export interface AdminOnboardingResult {
  onboardedAt: number | null;
}

/**
 * The two calls that read and write the caller's OWN registry doc.
 *
 * They are the only blumeStaff callables that are not super-admin-only,
 * because they take no target: the uid comes off the verified token, so there
 * is no parameter here that could be pointed at somebody else. That is also
 * why the flag lives on the server at all rather than in localStorage - a
 * per-browser flag replays the tour on every new machine and profile, and a
 * tour that explains which roles YOU hold arriving as if you were new is worse
 * than no tour.
 */
export const adminGetOnboarding = adminCallable<Record<string, never>, AdminOnboardingResult>(
  'blumeAdmin_GetOnboarding',
);

/** Stamp the tour finished. Idempotent server-side - the first stamp wins. */
export const adminMarkOnboarded = adminCallable<Record<string, never>, { onboardedAt: number }>(
  'blumeAdmin_MarkOnboarded',
);

/**
 * Delete an admin outright - the registry doc AND their Firebase sign-in.
 *
 * Not a stronger revoke: it destroys the record that they ever had access, and
 * their Auth user is their whole Blume identity, so a member of any partner
 * loses the product too. Revoke is the ordinary removal; this is cleanup.
 */
export const adminDeleteStaff = adminCallable<AdminDeleteStaffParams, AdminDeleteStaffResult>(
  'blumeAdmin_DeleteStaff',
);

/**
 * Write the generated launch dataset - 364 documents in one transaction.
 *
 * Was a client-side transaction, which made it super-only whatever the
 * capability table said: rules give partners/, partners/{id}/users/ and invites/
 * to isSuper() alone. Server-side it is an ordinary capability, and the 371 KB
 * of generated data left the bundle with it.
 *
 * Slow by nature (364 writes) - the page keeps its own busy state.
 */
export const adminPopulateLaunch = adminCallable<
  AdminPopulateLaunchParams,
  AdminPopulateLaunchResult
>('blumeAdmin_PopulateLaunchDataset', { timeout: 120_000 });

/** Create a login: Auth user + `users/{emailLowercase}` doc, `adminCreated` stamped. */
export const adminCreateAccount = adminCallable<AdminCreateAccountParams, AdminCreateAccountResult>(
  'blumeAdmin_CreateAccount',
);

/** Fix an account's name - Firebase Auth and the doc stay in step. */
export const adminUpdateAccount = adminCallable<AdminUpdateAccountParams, AdminUpdateAccountResult>(
  'blumeAdmin_UpdateAccount',
);

/** Delete a login - Auth user and doc together; the backend owns the cascade. */
export const adminDeleteAccount = adminCallable<AdminDeleteAccountParams, AdminDeleteAccountResult>(
  'blumeAdmin_DeleteAccount',
);

/** Invite an email to a partner - pending profile + invite entry + best-effort email. */
export const adminInviteUser = adminCallable<AdminInviteUserParams, AdminInviteUserResult>(
  'blumeAdmin_InviteUser',
);

/** Revoke a PENDING invite - the entry and its placeholder profile go together. */
export const adminRevokeInvite = adminCallable<AdminInviteRefParams, AdminInviteRefResult>(
  'blumeAdmin_RevokeInvite',
);

/** Re-send a pending invite's email - failures throw (the email IS the payload). */
export const adminResendInvite = adminCallable<AdminInviteRefParams, AdminInviteRefResult>(
  'blumeAdmin_ResendInvite',
);

/** Disable/enable sign-in at the Auth level - live sessions drop on disable. */
export const adminSetAccountDisabled = adminCallable<
  AdminSetAccountDisabledParams,
  AdminSetAccountDisabledResult
>('blumeAdmin_SetAccountDisabled');

/** Remove an ACCEPTED member from a partner - pending invitees get revoke instead. */
export const adminRemoveUserFromPartner = adminCallable<
  AdminRemoveUserFromPartnerParams,
  AdminRemoveUserFromPartnerResult
>('blumeAdmin_RemoveUserFromPartner');

/** Per-partner deactivate/reactivate (the app's soft flag) - idempotent. */
export const adminSetPartnerUserDeactivated = adminCallable<
  AdminSetPartnerUserDeactivatedParams,
  AdminSetPartnerUserDeactivatedResult
>('blumeAdmin_SetPartnerUserDeactivated');

/** Manually stamp verification.emailVerifiedAt - the working verification path. */
export const adminVerifyAccount = adminCallable<AdminVerifyAccountParams, AdminVerifyAccountResult>(
  'blumeAdmin_VerifyAccount',
);

// ---------------------------------------------------- seed data (dev tools)

/**
 * The two `v3_*` seed callables predate the blumeAdmin_* surface and return a
 * soft `{ status }` envelope instead of throwing on business failures - the
 * Seed data page branches on `status === 200` and shows `error` otherwise.
 * Hard failures (offline, permission-denied from the super gate) still throw
 * `CallableError` like every other wrapper.
 *
 * Both functions run with `timeoutSeconds: 540`; the client timeout must
 * match, or the SDK's 70s default aborts live seeds that are still (happily)
 * running.
 */
const SEED_CALLABLE_TIMEOUT: HttpsCallableOptions = { timeout: 540_000 };

export interface SeedMockDataParams {
  partnerName: string;
  /** Omit / empty for the fixture's four default buildings. */
  schools?: string[];
  state?: string;
  partnerType?: string;
}
export interface SeedMockDataResult {
  status: number;
  error?: string;
  summary?: {
    authUsers: number;
    firestoreDocs: number;
    targetProject: string;
    partnerId: string;
    partnerName: string;
    emailDomain: string;
  };
}

export interface DeleteMockPartnerParams {
  partnerId: string;
}
export interface DeleteMockPartnerResult {
  status: number;
  error?: string;
  deleted?: number;
}

/** Seed (or re-seed) a demo partner from the mock fixture - destructive per partner. */
export const seedMockData = adminCallable<SeedMockDataParams, SeedMockDataResult>(
  'v3_seedMockData',
  SEED_CALLABLE_TIMEOUT,
);

/** Delete a demo partner the caller owns - subcollections, users, invites, Auth accounts. */
export const deleteMockPartner = adminCallable<DeleteMockPartnerParams, DeleteMockPartnerResult>(
  'v3_deleteMockPartner',
  SEED_CALLABLE_TIMEOUT,
);
